import React, { useEffect, useState } from 'react';
import { Route } from 'react-router';
import { useSelector } from 'react-redux';
import AuthService from '../services/authService';
import Loader from 'modus/components/misc/Loader';
import UnifiedAPIService from 'services/UnifiedAPIService';
import { CloudConsoleClient, IAMClient } from 'libs/utils/http/client/HttpClient';
import ConsumerClient, { ConsumerClient2 } from 'pages/home/consumer/service/ConsumerClient';
import TeamService from 'libs/resources/team/TeamService';
import ShareService from 'libs/resources/proxy/ShareService';

interface AuthState {
  user: {
    access_token?: string;
  } | null;
  loading: boolean;
  error: any;
}

interface RootState {
  auth: AuthState;
}

interface PrivateRouteProps {
  component: React.ComponentType<any>;
  [key: string]: any;
}

const PrivateRoute = ({ component: Component, ...rest }: PrivateRouteProps) => {
  const { user, loading } = useSelector((state: RootState) => state.auth);
  const [tokensConfigured, setTokensConfigured] = useState(false);
  const isAuthenticated = !!user?.access_token;

  const savePath = () => {
    const currentPath = window.location.pathname + window.location.search;
    const excludedPaths = ['/landing', '/login', '/callback', '/error'];
    
    if (excludedPaths.some(path => currentPath.includes(path))) {
      return;
    }

    if (!localStorage.getItem('LoadedUrl')) {
      localStorage.setItem('LoadedUrl', currentPath);
    }
  };

  // Save path on mount and when auth state changes
  useEffect(() => {
    savePath();
    window.addEventListener('beforeunload', savePath);
    return () => window.removeEventListener('beforeunload', savePath);
  }, []);

  // Configure service tokens when access token is available
  useEffect(() => {
    if (user?.access_token) {
      // Set tokens for all services
      UnifiedAPIService.setToken?.(user.access_token);
      CloudConsoleClient.useToken?.(user.access_token);
      ConsumerClient.useToken?.(user.access_token);
      ConsumerClient2.useToken?.(user.access_token);
      IAMClient.useToken?.(user?.access_token);
      TeamService.useToken(user.access_token);
      ShareService.useToken(user.access_token);
      
      // Mark tokens as configured
      setTokensConfigured(true);
    } else {
      setTokensConfigured(false);
    }
  }, [user?.access_token]);

  // Handle authentication
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      AuthService.login();
    }
  }, [isAuthenticated, loading]);

  return (
    <Route {...rest} render={(props: any) => {
      if (loading) {
        return <Loader showBackDrop message="Authenticating..." />;
      }
      
      if (!isAuthenticated) {
        return <Loader showBackDrop message="Redirecting to login..." />;
      }

      if (!tokensConfigured) {
        return <Loader showBackDrop message="Configuring application..." />;
      }

      return (
        <Component {...props} key="new-component">
          {props.children}
        </Component>
      );
    }} />
  );
};

export default PrivateRoute;